import React, { useEffect, useRef } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { Cast, StopScreenShare } from "@material-ui/icons";
import { useDispatch, useSelector } from "../../../../store";
import {
  setScreenShareStream,
  toggleScreenShare,
} from "../../../../slices/roomSlice";
import * as webRTCHandler from "../../../../utils/webRTCHandler";

// Multi-party (bot + humans): same readable resolution as 2-party; save CPU mainly via FPS cap.
const getScreenShareConstraints = (isMultiParty) => ({
  video: {
    cursor: "always",
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    frameRate: isMultiParty
      ? { ideal: 15, max: 24 }
      : { ideal: 15, max: 30 },
  },
  audio: false,
});


const SwitchToScreenShareButton = () => {
  const { screenShareActive, screenShareStream, screenSharedBy, roomId, flutterApp, platform, participants, calleeEmail } = useSelector(
    (state) => state.room
  );

  const isAndroid = platform === "android";

  const dispatch = useDispatch();

  const lastSentRef = useRef(0);
  const startClickSeenRef = useRef(false);
  const stopClickSeenRef = useRef(false);
  const startTimerRef = useRef(null);
  const stopTimerRef = useRef(null);

  useEffect(() => {
    console.log("USER platform", platform);
    if (screenShareActive) {
      webRTCHandler.toggleScreenShare(screenShareActive, screenShareStream);
    } else {
      webRTCHandler.toggleScreenShare(screenShareActive);
    }
  }, [screenShareActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScreenShare = async () => {
    try {
      if (!screenShareActive) {
        const isMultiParty = participants.length >= 3;
        const constraints = getScreenShareConstraints(isMultiParty);
        const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
        if (stream) {
          const [videoTrack] = stream.getVideoTracks();
          if (videoTrack) {
            videoTrack.contentHint = "detail";
            if (typeof videoTrack.applyConstraints === "function") {
              await videoTrack.applyConstraints(constraints.video);
            }
          }
          videoTrack?.addEventListener("ended", () => {
            dispatch(toggleScreenShare(false));
            dispatch(setScreenShareStream(null));
          });
          dispatch(setScreenShareStream(stream));
          dispatch(toggleScreenShare(true));
        }
      } else {
        screenShareStream.getTracks().forEach((track) => track.stop());
        dispatch(toggleScreenShare(false));
        dispatch(setScreenShareStream(null));
      }
    } catch (error) {
      // console.log("error while getting screen share stream", error);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleScreenShare();
  };

  const checkIfSharingActiveForCurrentUser = () => {
    if (screenSharedBy) {
      return false;
    }
    return true;
  };

  const onMobileShareScreenClick = () => {
    startClickSeenRef.current = true;
    if (startTimerRef.current) window.clearTimeout(startTimerRef.current);
    sendToFlutter("screen_share_request");
  };

  const onMobileShareScreenTouchEnd = (e) => {
    e.preventDefault();
    sendToFlutter("screen_share_request");
    if (!flutterApp) {
      scheduleClickFallback("iconnectshare-screen-button", startClickSeenRef, startTimerRef);
    }
  };
  
  const onMobileStopScreenShareClick = () => {
    stopClickSeenRef.current = true;
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    sendToFlutter("screen_share_stop_request");
  };

  const onMobileStopScreenShareTouchEnd = (e) => {
    e.preventDefault();
    sendToFlutter("screen_share_stop_request");
    if (!flutterApp) {
      scheduleClickFallback("iconnectshare-screen-stop-button", stopClickSeenRef, stopTimerRef);
    }
  };

  const sendToFlutter = (type) => {
    if (flutterApp) {
    const socketId = participants?.length > 0 ? participants[0].socketId : "";
    const now = Date.now();

    // Debounce to avoid touchend + click double sending
    if (now - lastSentRef.current < 800) return;
    lastSentRef.current = now;

    try {
      if (window.AppBridge && window.AppBridge.postMessage) {
        window.AppBridge.postMessage(
          JSON.stringify({
            type,
            ts: now,
            page: window.location.href,
            roomId,
            socketId,
            email: calleeEmail,
            flutterApp,
          })
        );
      }
    } catch (_) {}
  }
  }

  const scheduleClickFallback = (buttonId, clickSeenRef, timerRef) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    clickSeenRef.current = false;

    timerRef.current = window.setTimeout(() => {
      if (!clickSeenRef.current) {
        document.getElementById(buttonId)?.click();
      }
    }, 3000);
  };

  return (
    <Box>
      {/* MOBILE-SIZED SCREENS: show the two static buttons with specific IDs (CSS media query controlled) */}
      <Box
        sx={{
          display: { xs: "block", md: "none" }, // Show on mobile (xs), hide on desktop (md+)
        }}
      >

        <Button
          id="iconnectshare-screen-button"
          sx={{
            color: "white",
            boxShadow: "none",
            ...(isAndroid && screenShareActive && { display: "none" }),
          }}
          variant="contained"
          {...(isAndroid
            ? { onClick: handleScreenShare }
            : { onTouchEnd: onMobileShareScreenTouchEnd })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Cast />
            <Typography sx={{ mt: 0, fontSize: "12px" }}>
              Share Screen
            </Typography>
          </Box>
        </Button>

        <Button
          id="iconnectshare-screen-stop-button"
          sx={{
            color: "white",
            boxShadow: "none",
            display: isAndroid ? (screenShareActive ? "block" : "none") : "none",
          }}
          variant="contained"
          {...(isAndroid
            ? { onClick: handleScreenShare }
            : { onTouchEnd: onMobileStopScreenShareTouchEnd })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <StopScreenShare />
            <Typography sx={{ mt: 0, fontSize: "12px" }}>
              Stop Sharing 
            </Typography>
          </Box>
        </Button>
        <div id="iconnect-room-id" style={{ display: "none" }}>{roomId}</div>
        <div id="iconnect-user-socket-id" style={{ display: "none" }}>{participants.length > 0 ? participants[0].socketId : ""}</div>
        <div id="iconnect-user-email" style={{ display: "none" }}>{calleeEmail}</div>
        <div id="iconnect-flutter-app" style={{ display: "none" }}>{flutterApp}</div>
      </Box>

      {/* DESKTOP-SIZED SCREENS: keep existing behavior exactly the same (CSS media query controlled) */}
      <Box
        sx={{
          display: { xs: "none", md: "block" }, // Hide on mobile (xs), show on desktop (md+)
        }}
      >
        {checkIfSharingActiveForCurrentUser() ? (
          <Button
            sx={{
              color: "white",
              boxShadow: "none",
            }}
            variant="contained"
            onClick={handleScreenShare}
            // onTouchEnd={handleTouchEnd}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {screenShareActive ? <StopScreenShare /> : <Cast />}
              <Typography sx={{ mt: 0, fontSize: "12px" }}>
                {screenShareActive ? "Stop Sharing" : "Share Screen"}
              </Typography>
            </Box>
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default SwitchToScreenShareButton;
